/**
 * Requirements Summary:
 * Private lesson packages are lesson credits, not appointment counts. Keep
 * admin, parent, payment, receipt, and support copy aligned around each
 * program's per-length credit costs (a lesson length's option row says how
 * many credits one booking uses). When a caller can't supply the program's
 * duration options, copy falls back to the legacy rule: 1 credit covers one
 * 60-minute lesson or two 30-minute lessons.
 */

import { formatCurrency } from '@/utils/formatters/currencyUtils';
import {
  defaultCreditCostForDuration,
  formatCreditCostText,
  type DurationChoice,
} from '@/utils/private-lessons/durationOptions';

type PackageCreditLabelAudience = 'admin' | 'parent' | 'compact' | 'payment';

export interface PrivateLessonPackageLike {
  sessions: number;
  price?: number | string | null;
}

/**
 * A program's active lesson length with its per-booking credit cost — the
 * shared `DurationChoice` shape, with the price (for savings copy) optional.
 */
export type DurationCreditChoice = Omit<DurationChoice, 'price'> & {
  /** Dollars — the single-booking price for this length (for savings copy). */
  price?: number | null;
};

export interface PackagePriceBreakdownOptions {
  has30MinuteLessons?: boolean;
  has60MinuteLessons?: boolean;
  regular30MinutePrice?: number | null;
  regular60MinutePrice?: number | null;
  /** Per-option path: when provided (non-empty), replaces the 30/60 flags. */
  durationChoices?: DurationCreditChoice[] | null;
}

export interface PackageCoverageOptions {
  prefix?: string;
  has30MinuteLessons?: boolean;
  has60MinuteLessons?: boolean;
  /** Per-option path: when provided (non-empty), replaces the 30/60 flags. */
  durationChoices?: DurationCreditChoice[] | null;
}

function usableDurationChoices(
  choices?: DurationCreditChoice[] | null,
): DurationCreditChoice[] {
  if (!Array.isArray(choices)) return [];
  return choices
    .filter((choice) => Number(choice?.duration_minutes) > 0 && Number(choice?.credit_cost) > 0)
    .sort((a, b) => Number(a.duration_minutes) - Number(b.duration_minutes));
}

function joinSentenceParts(parts: string[]): string {
  if (parts.length <= 1) return parts.join('');
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
}

export function formatCreditCount(credits: number): string {
  if (!Number.isFinite(credits)) return '0';

  if (Number.isInteger(credits)) return String(credits);

  return credits.toFixed(2).replace(/\.?0+$/, '');
}

export function formatCreditNoun(credits: number): string {
  return credits === 1 ? 'credit' : 'credits';
}

export function formatCreditQuantity(credits: number): string {
  return `${formatCreditCount(credits)} ${formatCreditNoun(credits)}`;
}

export function formatPrivateLessonPackageLabel(
  credits: number,
  audience: PackageCreditLabelAudience = 'parent',
): string {
  const count = formatCreditCount(credits);

  switch (audience) {
    case 'admin':
      return `${count} lesson ${formatCreditNoun(credits)}`;
    case 'compact':
      return formatCreditQuantity(credits);
    case 'payment':
    case 'parent':
    default:
      return `${count}-credit lesson pack`;
  }
}

/**
 * Plain-English rule for what one booking costs in credits. With the
 * program's duration options: "A 30-minute lesson uses ½ credit and a
 * 60-minute lesson uses 1 credit." Without them, the legacy 30/60 rule.
 */
export function getPrivateLessonCreditRuleText(
  durationChoices?: DurationCreditChoice[] | null,
): string {
  const choices = usableDurationChoices(durationChoices);
  if (choices.length > 0) {
    const parts = choices.map(
      (choice) => `a ${choice.duration_minutes}-minute lesson uses ${formatCreditCostText(choice.credit_cost)}`,
    );
    const sentence = joinSentenceParts(parts);
    return `${sentence.charAt(0).toUpperCase()}${sentence.slice(1)}.`;
  }
  return '1 credit covers one 60-minute lesson or two 30-minute lessons.';
}

function joinCoverageParts(prefix: string, credits: number, coverageParts: string[]): string {
  if (coverageParts.length === 0) {
    return `${prefix} ${formatCreditQuantity(credits)}.`;
  }
  if (coverageParts.length === 1) {
    return `${prefix} ${coverageParts[0]}.`;
  }
  return `${prefix} ${coverageParts.join(', ')}, or a mix.`;
}

export function formatPrivateLessonPackageCoverage(
  credits: number,
  options: PackageCoverageOptions = {},
): string {
  const prefix = options.prefix ?? 'Good for';

  // Per-option path: one coverage part per lesson length the program offers,
  // using that length's real credit cost (e.g. a 4-credit pack with 25-minute
  // lessons at ½ credit → "8 25-minute lessons").
  const choices = usableDurationChoices(options.durationChoices);
  if (choices.length > 0) {
    const coverageParts = choices
      .map((choice) => {
        const lessons = Math.floor((credits + 1e-9) / Number(choice.credit_cost));
        if (lessons < 1) return null;
        return `${formatCreditCount(lessons)} ${choice.duration_minutes}-minute ${lessons === 1 ? 'lesson' : 'lessons'}`;
      })
      .filter((part): part is string => part !== null);
    return joinCoverageParts(prefix, credits, coverageParts);
  }

  const has30 = options.has30MinuteLessons !== false;
  const has60 = options.has60MinuteLessons !== false;
  const oneHourLessons = formatCreditCount(credits);
  const halfHourLessons = formatCreditCount(credits * 2);
  const coverageParts: string[] = [];

  if (has60) {
    coverageParts.push(`${oneHourLessons} ${oneHourLessons === '1' ? 'one-hour lesson' : 'one-hour lessons'}`);
  }

  if (has30) {
    coverageParts.push(`${halfHourLessons} ${halfHourLessons === '1' ? 'half-hour lesson' : 'half-hour lessons'}`);
  }

  return joinCoverageParts(prefix, credits, coverageParts);
}

export function formatPrivateLessonPackageProductName(credits: number, athleteName: string): string {
  return `${formatPrivateLessonPackageLabel(credits, 'payment')} - ${athleteName}`;
}

export function formatPrivateLessonPackagePaymentDescription(
  programName: string,
  credits: number,
  athleteName: string,
): string {
  return `${programName} - ${formatPrivateLessonPackageLabel(credits, 'payment')} for ${athleteName}`;
}

export function formatPrivateLessonPackagePurchaseNote(credits: number): string {
  return `${formatPrivateLessonPackageLabel(credits, 'payment')} purchase`;
}

export function getPrivateLessonPackagePriceBreakdown(
  pkg: PrivateLessonPackageLike,
  options: PackagePriceBreakdownOptions = {},
): {
  perCredit: number;
  per30MinuteLesson: number;
  per60MinuteLesson: number;
  parts: string[];
} | null {
  const price = Number(pkg.price);

  if (!Number.isFinite(price) || price <= 0 || pkg.sessions <= 0) {
    return null;
  }

  const perCredit = price / pkg.sessions;
  const parts: string[] = [`${formatCurrency(perCredit)} per credit`];

  // Per-option path: one per-lesson price line per lesson length, priced by
  // that length's real credit cost, with savings vs. its single-booking price.
  const choices = usableDurationChoices(options.durationChoices);
  if (choices.length > 0) {
    for (const choice of choices) {
      const perLesson = perCredit * Number(choice.credit_cost);
      parts.push(
        `${formatCurrency(perLesson)} per ${choice.duration_minutes}-min lesson${formatSavingsLabel(perLesson, choice.price)}`,
      );
    }
    const choice30 = choices.find((choice) => Number(choice.duration_minutes) === 30);
    const choice60 = choices.find((choice) => Number(choice.duration_minutes) === 60);
    return {
      perCredit,
      per30MinuteLesson: perCredit * Number(choice30?.credit_cost ?? defaultCreditCostForDuration(30)),
      per60MinuteLesson: perCredit * Number(choice60?.credit_cost ?? defaultCreditCostForDuration(60)),
      parts,
    };
  }

  const per30MinuteLesson = perCredit * 0.5;
  const per60MinuteLesson = perCredit;
  const has30 = options.has30MinuteLessons !== false;
  const has60 = options.has60MinuteLessons !== false;

  if (has30) {
    parts.push(`${formatCurrency(per30MinuteLesson)} per 30-min lesson${formatSavingsLabel(per30MinuteLesson, options.regular30MinutePrice)}`);
  }

  if (has60) {
    parts.push(`${formatCurrency(per60MinuteLesson)} per 60-min lesson${formatSavingsLabel(per60MinuteLesson, options.regular60MinutePrice)}`);
  }

  return {
    perCredit,
    per30MinuteLesson,
    per60MinuteLesson,
    parts,
  };
}

export function formatCreditsRemaining(credits: number): string {
  return `${formatCreditQuantity(credits)} remaining`;
}

function formatSavingsLabel(packageLessonPrice: number, regularLessonPrice?: number | null): string {
  if (!regularLessonPrice || regularLessonPrice <= 0) return '';

  const pct = Math.round((1 - packageLessonPrice / regularLessonPrice) * 100);

  if (pct > 0) return ` (${pct}% off)`;
  if (pct < 0) return ` (${Math.abs(pct)}% more)`;
  return '';
}

/**
 * The staff booking window's credit line (#6245), e.g.
 * "Uses 1 of Ava's 3 lesson credits."
 */
export function staffBookingCreditUseText(params: { creditCost: number; athleteFirstName: string; creditsLeft: number }): string {
  const { creditCost, athleteFirstName, creditsLeft } = params;
  // Natural fractions ("½"), the same notation as the credit rule line.
  const amount = (credits: number) => formatCreditCostText(credits).replace(/ credits?$/, '');
  return `Uses ${amount(creditCost)} of ${athleteFirstName}'s ${amount(creditsLeft)} lesson ${formatCreditNoun(creditsLeft)}.`;
}
